/**
 * useBodyScrollLock.ts
 *
 * Locks document scrolling while any caller wants it locked, and restores exactly what
 * was there before once the last one lets go.
 *
 * ── Why this is shared rather than two lines in each overlay ─────────────────────
 * `document.body.style.overflow` is one slot on one node, and this app has several
 * overlays that want it at once. Before this hook, `Modal` set `'hidden'` on open and
 * wrote `''` on close, while the AI sidebar captured the previous value and restored
 * it — the two are individually reasonable and wrong together. Open the AI overlay
 * (body becomes `hidden`), open a modal from inside it, close the modal: the modal's
 * cleanup writes `''` and the page is scrollable again underneath an overlay that is
 * still open and still `fixed`. Last-writer-wins across independent components cannot
 * be made correct by making each component more careful; it needs one owner.
 *
 * The count is module scope for the same reason the one in `useVisualViewportVars` is:
 * the resource being guarded is a single global property, so its bookkeeping has to be
 * a singleton too. A context would scope it to a subtree, which is precisely wrong for
 * a document-level lock.
 *
 * ── Which element actually scrolls, and why that decides the technique ───────────
 * `Layout.tsx` runs three tiers and they do not share a scroller:
 *
 *   < 768px    the shell releases its viewport lock (`max-md:h-auto
 *              max-md:overflow-visible`), so the DOCUMENT scrolls.
 *   768–1023   the shell's `<main data-app-scroller>` scrolls.
 *   >= 1024    the same, and this tier is deliberately left alone.
 *
 * `overflow: hidden` on `<body>` therefore only means anything on the first of those,
 * and even there it does not hold on iOS Safari: touch-dragging still scrolls the
 * document. The technique that does work is taking the body out of flow at its current
 * offset — `position: fixed; top: -scrollY` — and putting the offset back on release.
 * SCRUM-1423 asked for both halves: "background page scroll should be locked" and
 * "the page should return to the exact scroll position it was at before".
 *
 * That technique is also why the tier matters so much. At `md` and above the shell is
 * `h-dvh overflow-hidden` and the body is not the scrollport; making it `fixed` there
 * would take the whole app out of flow to fix a scroll that was never happening. So
 * the strategy is chosen from the tier, recorded, and reversed with its own undo.
 *
 * The tier is sampled once when the lock is taken (`isBelowNow`) rather than
 * subscribed to. A viewport cannot cross a breakpoint while a modal is open often
 * enough to matter, and cleanup reverses whatever was actually applied, so a rotation
 * mid-lock unwinds correctly even though it does not re-strategise.
 *
 * ── What this still does not fix ────────────────────────────────────────────────
 * There are ~20 other call sites in the app still writing `document.body.style.overflow`
 * directly (`pages/leads/LeadDetailView.tsx`, `pages/patients/files/FileLightbox.tsx`,
 * several marketing pages). They have the same conflict with each other that this hook
 * resolves for its callers. Converting them is a separate change —
 * `pages/patients/PatientCreateModal.tsx` already carries a comment describing the
 * hazard, so the problem is known and is not this batch's to finish.
 */
import { useEffect } from 'react';
import { isBelowNow } from './useBreakpoint';

/** How many callers currently want the document locked. */
let lockCount = 0;

/**
 * What the first lock did, so the last release can undo exactly that and no more.
 *
 * Every variant carries `overflow` — the body value captured before the lock — because
 * the body write happens at every tier. The extra fields are the tier-specific part.
 */
type Applied =
  | { kind: 'none' }
  /** Desktop, and any route with no app shell: the body write and nothing else. */
  | { kind: 'body'; overflow: string }
  /** Phone tier: body taken out of flow at `scrollY`, offset restored on release. */
  | { kind: 'document'; overflow: string; position: string; top: string; width: string; scrollY: number }
  /** Tablet tier: the app's own scrollport clamped; its offset holds by itself. */
  | { kind: 'element'; el: HTMLElement; overflow: string; elOverflow: string };

let applied: Applied = { kind: 'none' };

export function useBodyScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;
    if (typeof document === 'undefined') return;

    lockCount += 1;
    if (lockCount === 1) {
      applied = engage();
    }

    return () => {
      lockCount -= 1;
      // Anyone else still holding the lock keeps the page locked.
      if (lockCount > 0) return;

      release(applied);
      applied = { kind: 'none' };
    };
  }, [locked]);
}

function engage(): Applied {
  const { body } = document;

  // Always taken, at every tier, exactly as before this change. Purely additive on
  // purpose: not every route renders inside the app shell — the login page and the
  // public e-form routes have no `<main>` and scroll the document at ANY width — so
  // dropping the body write above `md` because the shell usually owns scrolling there
  // would quietly regress those. It costs nothing where it is already a no-op.
  const overflow = body.style.overflow;
  body.style.overflow = 'hidden';

  // Phone tier: the document is the scrollport (the shell releases its lock below md).
  // `overflow: hidden` alone does not hold on iOS Safari — touch-dragging still scrolls
  // it — so pin the body at its current offset and put the offset back on release.
  if (isBelowNow('md')) {
    const scrollY = window.scrollY;
    const before = {
      kind: 'document' as const,
      overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      scrollY,
    };

    body.style.position = 'fixed';
    // Negative, so the same pixels stay under the backdrop rather than jumping to top.
    body.style.top = `-${scrollY}px`;
    // `fixed` collapses the body to its content width without this.
    body.style.width = '100%';
    return before;
  }

  // Tablet tier: the shell's own scrollport is what moves, and the body write above is
  // a no-op against it. Clamp it directly. No `position` games — it is already a
  // bounded box, so hiding its overflow stops the scroll and holds its offset for free.
  if (isBelowNow('lg')) {
    const el = document.querySelector<HTMLElement>('[data-app-scroller]');
    if (el) {
      const before = { kind: 'element' as const, el, overflow, elOverflow: el.style.overflow };
      el.style.overflow = 'hidden';
      return before;
    }
  }

  // Desktop: the body write above and nothing more. The page scrolling behind a modal
  // at >= 1024px is existing, documented behaviour and changing it would change all of
  // Modal's consumers at once.
  return { kind: 'body', overflow };
}

function release(state: Applied): void {
  const { body } = document;

  if (state.kind === 'none') return;

  // Every branch restores the body overflow it captured.
  body.style.overflow = state.overflow;

  if (state.kind === 'document') {
    body.style.position = state.position;
    body.style.top = state.top;
    body.style.width = state.width;
    // Put the reader back where they were. Must follow the style reset, or the document
    // is still out of flow and has nothing to scroll.
    window.scrollTo(0, state.scrollY);
    return;
  }

  if (state.kind === 'element') {
    state.el.style.overflow = state.elOverflow;
  }
}
